import "@splidejs/react-splide/css";
import React, { FunctionComponent, useCallback, useEffect } from "react";
import { Toaster } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import "react-modern-drawer/dist/index.css";
import { BrowserRouter } from "react-router-dom";
import "swiper/css";
import "swiper/css/thumbs";

import { HttpHeaderNames } from "@touchtech/web-api-client";
import { Logger } from "@touchtech/web-logging";
import { ServiceWorkerProvider } from "@touchtech/web-service-worker-provider";
import { TenantIdProvider } from "@touchtech/web-tenant-id-provider";

import ErrorBoundary from "@is/components/ErrorBoundary";
import ThemeWrapper from "@is/components/ThemeWrapper";
import { APP_PATH, LocalStorageKeys, TranslationContext } from "@is/constants";
import { ViewStateProvider } from "@is/contexts";
import { SessionTransitionProvider } from "@is/contexts/SessionTransitionContext";
import { UserActivityProvider } from "@is/contexts/UserActivityContext";
import UserActivityModal from "@is/features/Activities/UserActivityModal";
import OfflineView from "@is/features/OfflineView";
import { useDisableGestures, useStoreLanguage, useUnload } from "@is/hooks";
import useOnlineStatus from "@is/hooks/useOnlineStatus";
import { useWebSocketReconnectTelemetry } from "@is/hooks/websocket";
import { LocalStorageService } from "@is/services";
import { api } from "@is/services/api";
import {
  getLanguageFromLocalStorage,
  installBlobUrlTelemetry,
  installEarlyErrorCapture,
  logAppStart,
  markAppStartCompleted,
  markBootStage,
  stopEarlyErrorCapture,
} from "@is/utils";
import { getEnvValue } from "@is/utils/env";

import "../i18n/config";
import AppRoutes from "../routes";
import { terminateWorkers } from "../workers";
import css from "./index.module.css";

installBlobUrlTelemetry();

Logger.initialize(
  {
    token: getEnvValue("VITE_AXIOM_TOKEN"),
    orgId: getEnvValue("VITE_AXIOM_ORGANIZATION_ID"),
    dataset: getEnvValue("VITE_AXIOM_DATASET"),
  },
  getEnvValue("VITE_ENVIRONMENT")
);
markBootStage("logger-initialized");

logAppStart();
installEarlyErrorCapture();
markBootStage("app-start-logged");

const App: FunctionComponent = () => {
  const languageCode = getLanguageFromLocalStorage();
  const { isOnline } = useOnlineStatus();

  const { t } = useTranslation(TranslationContext.General);
  const { i18n } = useTranslation();
  const { storeLanguageCode } = useStoreLanguage(isOnline);

  const handleTenantIdChanged = (tenantId: string | null) => {
    api.setHeader(HttpHeaderNames.TENANT_ID, tenantId as string);

    Logger.instance.setTags([`tenantId-${tenantId}`]);
  };

  const handleError = (errorMessage: string, errorInfo: unknown) => {
    Logger.instance.fatal(errorMessage, errorInfo);
  };

  // Reaching this effect means the initial render committed — flag the boot as
  // completed and stop the boot-window error capture (a whitescreen never gets
  // here, so its boot stays uncompleted and its error stays captured).
  useEffect(() => {
    markAppStartCompleted();
    stopEarlyErrorCapture();
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
    };

    document.addEventListener("gesturestart", handler);
    document.addEventListener("gesturechange", handler);
    document.addEventListener("gestureend", handler);
    return () => {
      document.removeEventListener("gesturestart", handler);
      document.removeEventListener("gesturechange", handler);
      document.removeEventListener("gestureend", handler);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      performance.clearResourceTimings?.();
      performance.clearMarks?.();
      performance.clearMeasures?.();
    }, 60_000);

    return () => clearInterval(interval);
  }, []);

  // memoized because onError prop of ServiceWorkerProvider is used
  // as a useEffect dependency
  const handleServiceWorkerProviderError = useCallback(
    (message: string, error: Error) => {
      Logger.instance.error(message, error);
    },
    []
  );

  useEffect(() => {
    if (languageCode) {
      i18n.changeLanguage(languageCode);
    } else if (storeLanguageCode) {
      i18n.changeLanguage(storeLanguageCode);
      LocalStorageService.setItem(
        LocalStorageKeys.LanguageCode,
        storeLanguageCode
      );
    }
  }, [i18n, languageCode, storeLanguageCode]);

  useDisableGestures();

  useWebSocketReconnectTelemetry();

  useUnload((e) => {
    e.preventDefault();

    terminateWorkers();

    LocalStorageService.removeItem(LocalStorageKeys.SearchId);
  });

  return (
    <div className={css.container}>
      <ServiceWorkerProvider
        disabled={getEnvValue("VITE_SERVICE_WORKER") !== "true"}
        onError={handleServiceWorkerProviderError}
        swSrc={`${APP_PATH}/sw.js`}
      >
        {isOnline ? (
          <ErrorBoundary onError={handleError} t={t}>
            <Toaster />
            <TenantIdProvider onTenantIdChanged={handleTenantIdChanged}>
              <ThemeWrapper>
                <ViewStateProvider>
                  <SessionTransitionProvider>
                    <UserActivityProvider>
                      <BrowserRouter basename={APP_PATH}>
                        <AppRoutes />
                        <UserActivityModal />
                      </BrowserRouter>
                    </UserActivityProvider>
                  </SessionTransitionProvider>
                </ViewStateProvider>
              </ThemeWrapper>
            </TenantIdProvider>
          </ErrorBoundary>
        ) : (
          <OfflineView />
        )}
      </ServiceWorkerProvider>
    </div>
  );
};

export default App;
